import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useFamilyPortalApiClient } from '../../auth/hooks/useFamilyPortalApiClient'
import { buildConsentConfigResponse, type ConsentConfigPayload } from '../../consent/consentConfig'
import {
  answersFromSecondaryState,
  isOutstandingSecondaryElection,
} from '../../consent/consentOptions/consentOptionsUtils'
import type {
  ConsentContext,
  SecondaryConsentRow,
  SecondaryConsentStateResponse,
  SecondaryElection,
} from '../../consent/consentOptions/types'
import {
  scopedStateKey,
  secondaryAnswerSubjects,
  subjectStateKey,
  type ConsentSubject,
} from '../../consent/consentSubjects'
import { fetchConnectSubjects } from '../../hrc/api/hrcClient'
import { hasHealthRecordsConnectionNeedingAttention } from '../../hrc/connectionAttention'
import { HEALTH_RECORDS_ACCESS_ITEM_ID } from '../../hrc/healthRecordsConsent'
import type { HrcConnectSubject } from '../../hrc/types'

export type OutstandingSecondaryConsentItem = {
  id: string
  title: string
}

type OutstandingSecondaryConsentsState = {
  loading: boolean
  items: OutstandingSecondaryConsentItem[]
  connectSubjects: HrcConnectSubject[]
  hasManageableHealthRecords: boolean
  healthRecordsLoadFailed: boolean
}

type OutstandingAnswer = SecondaryElection | boolean | undefined

function asElection(value: OutstandingAnswer): SecondaryElection | undefined {
  if (value === 'opt_in' || value === 'opt_out' || value === 'unset') return value
  if (value === true) return 'opt_in'
  if (value === false) return 'opt_out'
  return undefined
}

function isOutstanding(
  itemId: string,
  answers: Record<string, OutstandingAnswer>,
  dualSecondary: boolean,
  subjects: ConsentSubject[] | null
): boolean {
  if (subjects) {
    return subjects.some((subject) => answers[subjectStateKey(itemId, subject, dualSecondary)] === undefined)
  }
  if (dualSecondary) {
    return (
      isOutstandingSecondaryElection(asElection(answers[scopedStateKey(itemId, 'SELF')])) ||
      isOutstandingSecondaryElection(asElection(answers[scopedStateKey(itemId, 'DEPENDENT')]))
    )
  }
  return isOutstandingSecondaryElection(asElection(answers[scopedStateKey(itemId)]))
}

export function getOutstandingSecondaryConsentItems(
  rows: SecondaryConsentRow[],
  answers: Record<string, OutstandingAnswer>,
  dualSecondary: boolean,
  /** One answer row per subject when a guardian-covered Sibling is listed; null otherwise. */
  subjects: ConsentSubject[] | null = null
): OutstandingSecondaryConsentItem[] {
  const seen = new Set<string>()
  const outstanding: OutstandingSecondaryConsentItem[] = []

  rows.forEach((row) => {
    if (seen.has(row.item.id)) return
    seen.add(row.item.id)

    if (isOutstanding(row.item.id, answers, dualSecondary, subjects)) {
      outstanding.push({ id: row.item.id, title: row.item.title })
    }
  })

  return outstanding
}

function isAccepted(value: OutstandingAnswer): boolean {
  return asElection(value) === 'opt_in'
}

function requiresConnectionAttention(subject: HrcConnectSubject): boolean {
  return subject.connections === 0 || hasHealthRecordsConnectionNeedingAttention(subject)
}

function hasUsableConnection(subject: HrcConnectSubject): boolean {
  return subject.connections > 0
}

function getAcceptedHealthRecordsSubjects(
  context: ConsentContext,
  answers: Record<string, OutstandingAnswer>,
  subjects: HrcConnectSubject[]
): HrcConnectSubject[] {
  if (context.secondaryConsentDualSubjects === true) {
    return subjects.filter((subject) =>
      isAccepted(answers[scopedStateKey(HEALTH_RECORDS_ACCESS_ITEM_ID, subject.role === 'SELF' ? 'SELF' : 'DEPENDENT')])
    )
  }

  if (!isAccepted(answers[HEALTH_RECORDS_ACCESS_ITEM_ID])) return []

  const ownerRole =
    context.primaryConsentCheckboxVariant === 'comparator'
      ? 'SELF'
      : context.primaryConsentCheckboxVariant === 'guardian'
        ? 'PROBAND'
        : null

  if (ownerRole) return subjects.filter((subject) => subject.role === ownerRole)

  return subjects.length === 1 ? subjects : []
}

export function getOutstandingHealthRecordsConnectSubjects(
  context: ConsentContext,
  answers: Record<string, OutstandingAnswer>,
  subjects: HrcConnectSubject[]
): HrcConnectSubject[] {
  return getAcceptedHealthRecordsSubjects(context, answers, subjects).filter(requiresConnectionAttention)
}

function hasUsableAcceptedHealthRecordsSubject(
  context: ConsentContext,
  answers: Record<string, OutstandingAnswer>,
  subjects: HrcConnectSubject[]
): boolean {
  return getAcceptedHealthRecordsSubjects(context, answers, subjects).some(hasUsableConnection)
}

export function useOutstandingSecondaryConsents(
  enabled: boolean,
  connectEnabled = false
): OutstandingSecondaryConsentsState {
  const intl = useIntl()
  const apiClient = useFamilyPortalApiClient()
  const [state, setState] = useState<OutstandingSecondaryConsentsState>({
    loading: enabled,
    items: [],
    connectSubjects: [],
    hasManageableHealthRecords: false,
    healthRecordsLoadFailed: false,
  })

  useEffect(() => {
    if (!enabled) {
      setState({
        loading: false,
        items: [],
        connectSubjects: [],
        hasManageableHealthRecords: false,
        healthRecordsLoadFailed: false,
      })
      return
    }

    let cancelled = false
    setState({
      loading: true,
      items: [],
      connectSubjects: [],
      hasManageableHealthRecords: false,
      healthRecordsLoadFailed: false,
    })

    async function load() {
      try {
        const [contextRes, configRes] = await Promise.all([
          apiClient.get('/consents/context', undefined, true),
          apiClient.get('/config/consents/secondary'),
        ])

        if (cancelled) return
        if (!configRes.ok) {
          setState({
            loading: false,
            items: [],
            connectSubjects: [],
            hasManageableHealthRecords: false,
            healthRecordsLoadFailed: false,
          })
          return
        }

        const context = contextRes.ok ? ((await contextRes.json().catch(() => null)) as ConsentContext | null) : null
        // Avoid guessing single vs dual-subject consent state without context.
        if (!context) {
          setState({
            loading: false,
            items: [],
            connectSubjects: [],
            hasManageableHealthRecords: false,
            healthRecordsLoadFailed: false,
          })
          return
        }

        const configPayload = (await configRes.json()) as ConsentConfigPayload
        const config = buildConsentConfigResponse(configPayload, intl.locale)

        if (config.secondaryItems.length === 0) {
          setState({
            loading: false,
            items: [],
            connectSubjects: [],
            hasManageableHealthRecords: false,
            healthRecordsLoadFailed: false,
          })
          return
        }

        const params = new URLSearchParams({
          configId: config.configId,
          configVersionId: config.configVersionId,
          consentType: 'secondary',
        })
        const hasHealthRecordsAccess = config.secondaryItems.some(
          (row) => row.item.id === HEALTH_RECORDS_ACCESS_ITEM_ID
        )
        // Home cannot safely infer connection state when this request fails. Keep unrelated
        // consent items available, but show no HRC connect or management action until the roster loads.
        // This is a deliberate fail-closed display policy, covered by the hook tests.
        const [stateRes, connectSubjectsResult] = await Promise.all([
          apiClient.get(`/consents/secondary?${params.toString()}`),
          connectEnabled && hasHealthRecordsAccess
            ? fetchConnectSubjects(apiClient)
                .then((subjects) => ({ status: 'ready' as const, subjects }))
                .catch(() => ({ status: 'failed' as const, subjects: [] as HrcConnectSubject[] }))
            : Promise.resolve({ status: 'ready' as const, subjects: [] as HrcConnectSubject[] }),
        ])

        if (cancelled) return
        if (!stateRes.ok) {
          setState({
            loading: false,
            items: [],
            connectSubjects: [],
            hasManageableHealthRecords: false,
            healthRecordsLoadFailed: false,
          })
          return
        }

        const stateData = (await stateRes.json().catch(() => ({}))) as SecondaryConsentStateResponse
        if (cancelled) return

        const dualSecondary = context.secondaryConsentDualSubjects === true
        const subjects = secondaryAnswerSubjects(context, dualSecondary)
        const parsed = answersFromSecondaryState(stateData, subjects)
        const answers = subjects ? parsed.toggles : { ...parsed.toggles, ...parsed.elections }
        const outstanding = getOutstandingSecondaryConsentItems(config.secondaryItems, answers, dualSecondary, subjects)
        const connectSubjects = connectSubjectsResult.subjects
        const outstandingConnectSubjects = getOutstandingHealthRecordsConnectSubjects(context, answers, connectSubjects)
        setState({
          loading: false,
          items: outstanding,
          connectSubjects: outstandingConnectSubjects,
          hasManageableHealthRecords:
            outstandingConnectSubjects.length === 0 &&
            hasUsableAcceptedHealthRecordsSubject(context, answers, connectSubjects),
          healthRecordsLoadFailed: connectSubjectsResult.status === 'failed',
        })
      } catch {
        if (!cancelled)
          setState({
            loading: false,
            items: [],
            connectSubjects: [],
            hasManageableHealthRecords: false,
            healthRecordsLoadFailed: false,
          })
      }
    }

    load().finally(() => {
      if (!cancelled) {
        setState((current) =>
          current.loading
            ? {
                loading: false,
                items: current.items,
                connectSubjects: current.connectSubjects,
                healthRecordsLoadFailed: current.healthRecordsLoadFailed,
                hasManageableHealthRecords: current.hasManageableHealthRecords,
              }
            : current
        )
      }
    })

    return () => {
      cancelled = true
    }
  }, [apiClient, connectEnabled, enabled, intl.locale])

  return state
}
